import { TextCollectionFieldType } from '@scale/shared/types/params/textcollection';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';
import _ from 'lodash';
import { Breadcrumb } from './breadcrumbs';

export interface AutoCompleteQuery {
  fieldSet: string;
  field: string;
  search: string;
}

export function getAutoCompleteFieldSetResponses(
  annotations: Record<string, TextCollectionAnnotation>,
  breadcrumbs: Breadcrumb[],
  query: AutoCompleteQuery,
): Record<string, TextCollectionAnnotation>[] {
  if (query.search === '') {
    return [];
  }
  if (!breadcrumbs.length) {
    return getMatchingFieldSetResponses(annotations, query);
  }
  const breadcrumb = breadcrumbs[0];
  const fieldSetAnnotation = annotations[breadcrumb.id];
  if (!fieldSetAnnotation) {
    return [];
  }
  if (fieldSetAnnotation.type !== TextCollectionFieldType.FieldSet) {
    return [];
  }
  const nestedSearchResults = fieldSetAnnotation.response.map(nestedAnnotations => {
    return getAutoCompleteFieldSetResponses(nestedAnnotations, breadcrumbs.slice(1), query);
  });
  const flatResults = _.flatten(nestedSearchResults);
  const duplicates = new Set<string>();
  return flatResults.filter(annotations => {
    const annotation = annotations[query.field];
    if (!annotation || annotation.type !== TextCollectionFieldType.Text) {
      return false;
    }
    const isDuplicate = annotation.response.every(response => {
      return duplicates.has(response);
    });
    annotation.response.forEach(response => {
      duplicates.add(response);
    });
    return !isDuplicate;
  });
}

function getMatchingFieldSetResponses(
  annotations: Record<string, TextCollectionAnnotation>,
  query: AutoCompleteQuery,
): Record<string, TextCollectionAnnotation>[] {
  const annotation = annotations[query.fieldSet];
  if (!annotation || annotation.type !== TextCollectionFieldType.FieldSet) {
    return [];
  }
  const search = query.search.toLowerCase();
  const fieldSetAnnotations = annotation.response.filter(response => {
    const textFieldAnnotation = response[query.field];
    if (textFieldAnnotation.type !== TextCollectionFieldType.Text) {
      return false;
    }
    return textFieldAnnotation.response.some(value => {
      const result = value.toLowerCase();
      return result.startsWith(search) && result !== search;
    });
  });
  return fieldSetAnnotations;
}
